// Palette allowlist per Visual Page content type.
//
// The Visual Page Editor's sidebar shows three buckets: containers, elements
// and dynamic components. Which items each bucket exposes for a given
// page_type is defined here — one shared source of truth so the rule is
// obvious to change and easy to search for.
//
// Rules (approved for this task):
//   • Containers: same set for every page type.
//   • Elements:   the eight generic elements are common; `next-start-date`
//                 is scoped to course + conference pages.
//   • Dynamic:    `dynamic-quick-form` is common; the mentor / success-story
//                 / testimonials blocks are scoped to the pages that
//                 naturally back them.
//
// IMPORTANT: this rule table only controls what the palette OFFERS at
// insert time. The editor's render/save/load pipeline is fully driven by
// each node's `type` string, so a saved tree keeps rendering identically
// even if it contains node types that aren't in the current palette.

// Items that are always available regardless of page_type.
export const PALETTE_COMMON = {
  containers: ['section', 'row', 'column', 'card'],
  elements:   ['heading', 'text', 'image', 'button', 'video', 'divider', 'spacer', 'html'],
  dynamic:    ['dynamic-quick-form'],
};

// Additions layered on top of PALETTE_COMMON for a specific page_type.
// Only Course and Conference are user-facing page types today; if more
// are added, extend this map alongside VISUAL_PAGE_TYPES + the backend
// whitelist in PagesController.
export const PALETTE_BY_TYPE = {
  course: {
    elements: [],
    dynamic:  [
      'dynamic-mentors', 'dynamic-success-stories', 'dynamic-testimonials',
      // Centre-column pieces of the course template.
      'dynamic-course-overview', 'dynamic-what-you-learn',
      'dynamic-alumni', 'dynamic-course-cta',
      // Atomic Apply / Enroll CTA (design-system styled, SKU-driven URL).
      'dynamic-cta',
      // Live, read-only start date + weekly chat schedule from the course.
      'next-start-date',
    ],
  },
  conference: {
    elements: [],
    dynamic:  ['dynamic-mentors', 'next-start-date'],
  },
};

// Dynamic types that exist only when the source page actually carries the
// matching row in its `blocks[]`. Everything else in the course bucket is part
// of the template Page.js always renders (Body, Mentor, SidebarQuickForm,
// SuccessStories, Testimonials) and so is always offered.
export const BLOCK_BACKED_DYNAMIC = {
  // 'dynamic-what-you-learn' is now a structured, always-available component
  // (title + bullet list authored in the Inspector), not a source-block block.
  'dynamic-alumni':         'alumni',
  'dynamic-course-cta':     'course_cta',
};

// Filter a bucket's full type list down to what the current pageType allows.
// - `bucketName`  one of 'containers' | 'elements' | 'dynamic'
// - `allTypes`    the editor's full registry list for that bucket (e.g.
//                 ELEMENT_TYPES). Preserves original ordering.
// - `pageType`    the visual page's stored page_type. Falsy (no page_type
//                 stored — legacy rows, standalone editor without a host
//                 page) => the palette is unfiltered so old pages keep
//                 the pre-filter behaviour.
// - Unknown pageType (not in PALETTE_BY_TYPE) => only PALETTE_COMMON items
//                 for that bucket are shown; safe default that never leaks
//                 an unrelated dynamic block into a page it doesn't match.
// - `sourceBlocks` (optional) the `block_type` list of the page this visual
//                 page was built from, recorded on the root node at create
//                 time. When supplied, block-backed dynamic types are narrowed
//                 to the blocks that source actually has, so the panel shows
//                 the components applicable to THIS course rather than every
//                 component any course could have. Omitted (legacy pages, or
//                 non-dynamic buckets) => no narrowing, previous behaviour.
export function filterPalette(bucketName, allTypes, pageType, sourceBlocks) {
  if (!pageType) return allTypes;
  const common = PALETTE_COMMON[bucketName] || [];
  const specific = (PALETTE_BY_TYPE[pageType] || {})[bucketName] || [];
  const allowed = new Set([...common, ...specific]);
  const blocks = Array.isArray(sourceBlocks) ? sourceBlocks : null;
  return allTypes.filter((t) => {
    if (!allowed.has(t)) return false;
    const needsBlock = blocks && BLOCK_BACKED_DYNAMIC[t];
    return needsBlock ? blocks.includes(needsBlock) : true;
  });
}
